import { ArrowRightIcon } from "@heroicons/react/24/outline";
import type { HelpTopic } from "@type/contact";
import Link from "next/link";
import useTranslation from "next-translate/useTranslation";

import { ContactIcon } from "./contact-icon";
import { SectionHeading } from "./section-heading";

// Bez tematu "Kariera" (wersje obcojęzyczne) są 4 kafelki - jeden rząd bez luki
const TOPICS_PER_ROW_COMPACT = 4;

interface HelpTopicsProps {
  topics: HelpTopic[];
}

export const HelpTopics = ({ topics }: HelpTopicsProps) => {
  const { t } = useTranslation("contact");

  if (topics.length === 0) {
    return null;
  }

  return (
    <section
      aria-labelledby="help-title"
      className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24 lg:px-8"
    >
      <SectionHeading
        eyebrow={t("help.eyebrow")}
        id="help-title"
        title={t("help.title")}
      />

      <ul
        className={`grid gap-3 sm:grid-cols-2 sm:gap-4 ${
          topics.length === TOPICS_PER_ROW_COMPACT
            ? "xl:grid-cols-4"
            : "xl:grid-cols-5"
        }`}
      >
        {topics.map((topic) => (
          <li key={topic.id}>
            <Link
              className="group flex h-full gap-4 rounded-xl border border-gray-200 bg-white p-5 transition duration-200 hover:-translate-y-0.5 hover:border-[#c1a166] hover:shadow-[0_16px_40px_-20px_rgba(5,17,36,0.35)] focus-visible:outline-2 focus-visible:outline-[#c1a166] focus-visible:outline-offset-2 xl:flex-col xl:p-6"
              href={topic.href}
            >
              <span className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-[#051124] text-[#c1a166] transition group-hover:bg-[#c1a166] group-hover:text-[#051124]">
                <ContactIcon className="size-5" name={topic.icon} />
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="font-semibold text-[#051124] leading-snug">
                  {t(`help.topics.${topic.id}.title`)}
                </span>
                <span className="mt-1.5 text-gray-600 text-sm leading-relaxed">
                  {t(`help.topics.${topic.id}.description`)}
                </span>
                <span className="mt-auto inline-flex items-center gap-1.5 pt-3 font-semibold text-[#8a6d2f] text-sm">
                  {t(`help.topics.${topic.id}.cta`)}
                  <ArrowRightIcon
                    aria-hidden="true"
                    className="size-4 transition group-hover:translate-x-0.5"
                  />
                </span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
};
